import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import suitcss from '../../../helpers/suitcss';
import SvgLoader from '../media/MediaSvgLoader';

const ComboBox = ({
                    id, label, options, onSelect, placeholder,
                  }) => {
  const [selected, setSelected] = useState('');
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const optionRefs = useRef([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (focusIdx !== -1 && optionRefs.current[focusIdx]) {
      optionRefs.current[focusIdx].focus();
    }
  }, [focusIdx]);

  const filteredOptions = options.filter((option) =>
    option.value.toLowerCase().includes(selected.toLowerCase()));

  if (!open && options.find((option) => option.value === selected)) {
    onSelect(selected);
  }

  const select = (value) => {
    setOpen(false);
    setSelected(value);
  };

  const name2IdAttr = (prefix, value) => {
    return `${prefix}-option-${value.replaceAll(' ', '')}`;
  };

  const handleKey = (event, value, prevIdx, nextIdx) => {
    const { key } = event;
    switch (key) {
      case 'Enter':
        select(value);
        setFocusIdx(-1);
        inputRef.current.focus();
        break;
      case 'Escape':
        if (open) {
          event.stopPropagation();
        }
        setOpen(false);
        setFocusIdx(-1);
        inputRef.current.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        setOpen(true);
        setFocusIdx(prevIdx);
        break;
      case 'ArrowDown':
        event.preventDefault();
        setOpen(true);
        setFocusIdx(nextIdx);
        break;
      default:
        inputRef.current.focus();
        setFocusIdx(-1);
    }
  };

  const selectedIdx = filteredOptions.findIndex(option => option.value === selected);
  const activeIdx = focusIdx !== -1 ? focusIdx : selectedIdx;

  return (
    <div
      className={suitcss({ componentName: 'ComboBox', modifiers: [open ? 'open' : 'closed'] }, this)}
    >
      <label
        htmlFor={id}
        className={suitcss({
          componentName: 'ComboBox',
          descendantName: 'label',
        })}
      >
        {label}
      </label>
      <input
        id={id}
        value={selected}
        ref={inputRef}
        placeholder={placeholder}
        onChange={(e) => {
          setOpen(true);
          setSelected(e.target.value);
        }}
        onKeyDown={(e) => handleKey(
          e,
          selected,
          filteredOptions.length > 0 ? filteredOptions.length - 1 : -1,
          filteredOptions.length > 0 ? 0 : -1,
        )}
        onClick={() => setOpen(true)}
        role="combobox"
        aria-controls={`${id}-list`}
        aria-expanded={open}
        autoComplete="off"
        aria-haspopup="listbox"
        aria-activedescendant={activeIdx !== -1 ? name2IdAttr(id, filteredOptions[activeIdx].value) : ''}
      />
      <div
        className={
          suitcss({
            componentName: 'ComboBox',
            descendantName: 'arrow',
          })
        }
      >
        <SvgLoader ariaHidden path="/icons/arrow-down.svg" />
      </div>
      <ul
        id={`${id}-list`}
        tabIndex={-1}
        role="listbox"
        className={suitcss({
          componentName: 'ComboBox',
          descendantName: 'autocomplete',
        })}
      >
        {open && filteredOptions.map((option, index) => (
          <li
            tabIndex={-1}
            ref={el => { optionRefs.current[index] = el; }}
            id={name2IdAttr(id, option.value)}
            className={suitcss({
              componentName: 'ComboBox',
              descendantName: 'autocompleteRow',
              className: option.classNames,
            })}
            role="option"
            aria-selected={false}
            onClick={() => select(option.value)}
            onKeyDown={(e) => handleKey(
              e,
              option.value,
              index > 0 ? index - 1 : filteredOptions.length - 1,
              filteredOptions.length > index + 1 ? index + 1 : 0,
            )}
          >
            {option.value}
          </li>
        ))}
      </ul>
    </div>
  );
};

ComboBox.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    classNames: PropTypes.array,
  })),
  onSelect: PropTypes.func,
};

export default ComboBox;
